import React, { useEffect, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  Avatar,
  Badge,
  Box,
  Collapse,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  InputGroup,
  InputLeftElement,
  Input,
  Spinner,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import CustomDrawer from 'components/ui/Drawer';
import Button from 'components/ui/Button';
import Dropdown, { DropdownItem } from 'components/ui/Dropdown';
import Steps, { Step as StepDef } from 'components/ui/Steps';
import MiniCalendar from 'components/ui/MiniCalendar';
import { useCreateAppointment } from 'hooks/useAppointments';
import { useCreateAvailability } from 'hooks/useAvailability';
import { useEpisodesByPatient } from 'hooks/useEpisodes';
import { usePatients } from 'hooks/usePatients';
import { planService } from 'services/planService';
import { serviceItemService } from 'services/serviceItemService';
import {
  ClinicalEpisode,
  CreateAppointmentDto,
  EstadoEpisodio,
  EstadoPlan,
  EstadoServicio,
  Patient,
  ServiceItem,
  TipoCita,
  TreatmentPlan,
} from 'types/models';
import {
  MdCalendarToday,
  MdClose,
  MdEventAvailable,
  MdPerson,
  MdSearch,
} from 'react-icons/md';

// ─── Step definitions ─────────────────────────────────────────────────────────

const DRAWER_STEPS: StepDef[] = [
  { id: 'patient',  title: 'Selecciona paciente' },
  { id: 'schedule', title: 'Configura el horario' },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialScheduledAt?: string;
  professionalId: string;
  onSuccess: () => void;
}

// ─── Patient list + search (step 1) ──────────────────────────────────────────

interface PatientSearchProps {
  onSelect: (p: Patient) => void;
}

function PatientSearch({ onSelect }: PatientSearchProps) {
  const [query, setQuery] = useState('');

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const inputBg     = useColorModeValue('gray.50', 'navy.700');
  const rowHover    = useColorModeValue('gray.50', 'navy.700');
  const mutedColor  = useColorModeValue('gray.400', 'gray.500');
  const textColor   = useColorModeValue('gray.800', 'white');

  const { data: patientsData, isLoading } = usePatients();
  const patients: Patient[] = patientsData ?? [];

  const filtered = patients.filter((p: Patient) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      p.nombres.toLowerCase().includes(q) ||
      p.apellidos.toLowerCase().includes(q) ||
      p.cedula.toLowerCase().includes(q)
    );
  });

  return (
    <Box>
      <InputGroup mb="10px">
        <InputLeftElement pointerEvents="none">
          <Icon as={MdSearch} color={mutedColor} />
        </InputLeftElement>
        <Input
          placeholder="Buscar por nombre o cédula..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          bg={inputBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="12px"
          fontSize="sm"
          _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
          autoFocus
        />
      </InputGroup>

      {isLoading && (
        <Flex justify="center" py="24px">
          <Spinner size="sm" color="brand.500" />
        </Flex>
      )}

      {!isLoading && filtered.length === 0 && (
        <Flex direction="column" align="center" py="24px" gap="8px">
          <Icon as={MdPerson} w="32px" h="32px" color={mutedColor} />
          <Text fontSize="sm" color={mutedColor}>
            {query ? `Sin resultados para "${query}"` : 'No hay pacientes registrados'}
          </Text>
        </Flex>
      )}

      {!isLoading && filtered.length > 0 && (
        <Box
          border="1px solid"
          borderColor={borderColor}
          borderRadius="12px"
          overflow="hidden"
          maxH="340px"
          overflowY="auto"
        >
          {filtered.map((p, i) => (
            <Flex
              key={p.id}
              align="center"
              gap="12px"
              px="14px"
              py="10px"
              cursor="pointer"
              borderTop={i > 0 ? '1px solid' : 'none'}
              borderTopColor={borderColor}
              _hover={{ bg: rowHover }}
              onClick={() => onSelect(p)}
            >
              <Avatar
                size="sm"
                name={`${p.nombres} ${p.apellidos}`}
                bg="brand.500"
                color="white"
              />
              <Box flex="1" minW={0}>
                <Text fontSize="sm" fontWeight="600" color={textColor} noOfLines={1}>
                  {p.nombres} {p.apellidos}
                </Text>
                <Text fontSize="xs" color={mutedColor} fontFamily="mono">{p.cedula}</Text>
              </Box>
            </Flex>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── Selected patient card ────────────────────────────────────────────────────

function PatientCard({ patient, onClear }: { patient: Patient; onClear: () => void }) {
  const borderColor = useColorModeValue('brand.200', 'brand.700');
  const bg          = useColorModeValue('brand.50', 'brand.900');
  const textColor   = useColorModeValue('gray.800', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');

  return (
    <Flex
      align="center" gap="12px" px="14px" py="10px"
      border="1px solid" borderColor={borderColor}
      bg={bg} borderRadius="12px"
    >
      <Avatar size="sm" name={`${patient.nombres} ${patient.apellidos}`} bg="brand.500" color="white" />
      <Box flex="1" minW={0}>
        <Text fontSize="sm" fontWeight="700" color={textColor} noOfLines={1}>
          {patient.nombres} {patient.apellidos}
        </Text>
        <Text fontSize="xs" color={mutedColor} fontFamily="mono">{patient.cedula}</Text>
      </Box>
      <Button size="xs" variant="ghost" onClick={onClear} leftIcon={<Icon as={MdClose} />}>
        Cambiar
      </Button>
    </Flex>
  );
}

// ─── Schedule step (step 2) ───────────────────────────────────────────────────

interface ScheduleStepProps {
  patient: Patient;
  professionalId: string;
  initialScheduledAt?: string;
  onSuccess: () => void;
  onBack: () => void;
  onClose: () => void;
}

function ScheduleStep({
  patient, professionalId, initialScheduledAt,
  onSuccess, onBack, onClose,
}: ScheduleStepProps) {
  const toast        = useToast();
  const createAppt   = useCreateAppointment();
  const createAvail  = useCreateAvailability();

  const [tipoCita,        setTipoCita]        = useState<TipoCita>(TipoCita.PRIMERA_VEZ);
  const [scheduledAt,     setScheduledAt]     = useState(initialScheduledAt ?? '');
  const [selectedDate,    setSelectedDate]    = useState<Date | null>(
    initialScheduledAt ? new Date(initialScheduledAt) : null
  );
  const [selectedTime,    setSelectedTime]    = useState<string>(
    initialScheduledAt
      ? (() => { const d = new Date(initialScheduledAt); const p = (n: number) => String(n).padStart(2,'0'); return `${p(d.getHours())}:${p(d.getMinutes())}`; })()
      : ''
  );
  const [episodeId,       setEpisodeId]       = useState('');
  const [planId,          setPlanId]          = useState('');
  const [motivo,          setMotivo]          = useState('');
  const [serviceId,       setServiceId]       = useState('');
  const [errors,          setErrors]          = useState<Record<string, string>>({});
  const [slotError,       setSlotError]       = useState('');
  const [missingAvail,    setMissingAvail]    = useState(false);
  const [availDuration,   setAvailDuration]   = useState(30);
  const [availCapacity,   setAvailCapacity]   = useState(1);

  const TIME_SLOTS: string[] = (() => {
    const slots: string[] = [];
    for (let h = 7; h <= 20; h++) {
      for (const m of [0, 30]) {
        if (h === 20 && m === 30) break;
        slots.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
      }
    }
    return slots;
  })();

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setSlotError('');
    setMissingAvail(false);
    if (selectedTime) {
      const [hh, mm] = selectedTime.split(':').map(Number);
      const dt = new Date(date);
      dt.setHours(hh, mm, 0, 0);
      setScheduledAt(dt.toISOString());
      setErrors(p => ({ ...p, scheduledAt: '' }));
    }
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setSlotError('');
    setMissingAvail(false);
    if (selectedDate) {
      const [hh, mm] = time.split(':').map(Number);
      const dt = new Date(selectedDate);
      dt.setHours(hh, mm, 0, 0);
      setScheduledAt(dt.toISOString());
      setErrors(p => ({ ...p, scheduledAt: '' }));
    }
  };

  const { data: serviceItems = [] } = useQuery<ServiceItem[]>({
    queryKey: ['catalog-services', 'active'],
    queryFn: serviceItemService.listAll,
    select: (items: ServiceItem[]) => items.filter((s: ServiceItem) => s.status === EstadoServicio.ACTIVO),
    staleTime: 15 * 60 * 1000,
    gcTime:    30 * 60 * 1000,
  });

  const handleServiceSelect = (id: string) => {
    setServiceId(id);
    const item = serviceItems.find((s: ServiceItem) => s.id === id);
    if (item && !motivo.trim()) setMotivo(item.name);
  };

  const requiresEpisode = tipoCita === TipoCita.SEGUIMIENTO || tipoCita === TipoCita.INTERCONSULTA;

  const { data: episodes = [] } = useEpisodesByPatient(patient.id);
  const activeEpisodes = episodes.filter(
    (e: ClinicalEpisode) => e.estado === EstadoEpisodio.ABIERTO || e.estado === EstadoEpisodio.EN_TRATAMIENTO,
  );

  const planQueries = useQueries({
    queries: activeEpisodes.map((ep: ClinicalEpisode) => ({
      queryKey: ['plans', 'episode', ep.id] as const,
      queryFn: () => planService.listByEpisode(patient.id, ep.id),
      select: (res: { data: TreatmentPlan[] }): TreatmentPlan[] => res.data,
    })),
  });

  const selectedEpisodeIdx = activeEpisodes.findIndex((ep: ClinicalEpisode) => ep.id === episodeId);
  const activePlans = (selectedEpisodeIdx >= 0 ? ((planQueries[selectedEpisodeIdx]?.data as TreatmentPlan[] | undefined) ?? []) : [])
    .filter((p: TreatmentPlan) => p.estado === EstadoPlan.ACTIVO);

  const episodeItems: DropdownItem[] = activeEpisodes.map((ep: ClinicalEpisode, i: number) => {
    const plans: TreatmentPlan[] = (planQueries[i]?.data as TreatmentPlan[] | undefined) ?? [];
    const hasActivePlan = plans.some((p: TreatmentPlan) => p.estado === EstadoPlan.ACTIVO);
    return {
      label: ep.motivoConsulta + (ep.diagnosticoPrincipal ? ` — ${ep.diagnosticoPrincipal}` : ''),
      value: ep.id,
      icon: hasActivePlan
        ? <Badge colorScheme="green" fontSize="9px" borderRadius="full" px="6px" flexShrink={0}>Plan activo</Badge>
        : undefined,
    };
  });

  const selectedEpisode = activeEpisodes.find((ep: ClinicalEpisode) => ep.id === episodeId);

  useEffect(() => { setEpisodeId(''); setPlanId(''); }, [tipoCita]);
  useEffect(() => { setPlanId(''); }, [episodeId]);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!scheduledAt) e.scheduledAt = 'Fecha y hora requeridas';
    else if (new Date(scheduledAt) <= new Date()) e.scheduledAt = 'No puede ser en el pasado';
    if (requiresEpisode && !episodeId) e.episodeId = 'Selecciona un episodio';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSlotError('');
    setMissingAvail(false);

    const payload: CreateAppointmentDto = {
      patientId: patient.id,
      professionalId,
      scheduledAt: new Date(scheduledAt).toISOString(),
      tipoCita,
      ...(episodeId     ? { episodeId }             : {}),
      ...(planId        ? { planId }               : {}),
      ...(motivo.trim() ? { motivo: motivo.trim() } : {}),
    };

    try {
      await createAppt.mutateAsync(payload);
      toast({ title: 'Cita agendada', status: 'success', duration: 2500, isClosable: true, position: 'top-right' });
      onSuccess();
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      const message: string = err?.response?.data?.message ?? '';
      if (status === 409) {
        setSlotError('Este slot ya alcanzó su capacidad máxima. Elige otra fecha u hora.');
      } else if (status === 422 && message.includes('no tiene disponibilidad configurada')) {
        setSlotError(message);
        setMissingAvail(true);
      } else if (status === 422) {
        setSlotError(message || 'El paciente no tiene tarjetero activo');
      } else {
        toast({ title: 'Error al agendar', description: message || 'Error inesperado', status: 'error', duration: 4000, isClosable: true, position: 'top-right' });
      }
    }
  };

  const DOW = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  const handleConfigureAvailability = async () => {
    if (!scheduledAt) return;
    const d = new Date(scheduledAt);
    const pad = (n: number) => String(n).padStart(2, '0');
    const startTime = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    const endDate = new Date(d.getTime() + availDuration * 60_000);
    const endTime = `${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;

    try {
      await createAvail.mutateAsync({
        professionalId,
        dayOfWeek: DOW[d.getDay()],
        startTime,
        endTime,
        slotDurationMinutes: availDuration,
        capacidadPorSesion: availCapacity,
      });
      setMissingAvail(false);
      setSlotError('');
      await handleSubmit();
    } catch (err: any) {
      toast({
        title: 'No se pudo configurar la disponibilidad',
        description: err?.response?.data?.message ?? 'Error inesperado',
        status: 'error', duration: 4000, isClosable: true, position: 'top-right',
      });
    }
  };

  const borderColor  = useColorModeValue('gray.200', 'whiteAlpha.200');
  const inputBg      = useColorModeValue('gray.50', 'navy.700');
  const mutedColor   = useColorModeValue('gray.500', 'gray.400');
  const labelColor   = useColorModeValue('gray.700', 'gray.200');
  const sectionColor = useColorModeValue('brand.500', 'brand.400');

  const chipBase = {
    px: '14px', py: '7px', borderRadius: '20px', fontSize: 'xs', fontWeight: '700',
    cursor: 'pointer', border: '1.5px solid', transition: 'all 0.15s',
  };
  const chip = (active: boolean) => ({
    ...chipBase,
    bg: active ? 'brand.500' : 'transparent',
    borderColor: active ? 'brand.500' : borderColor,
    color: active ? 'white' : mutedColor,
  });

  const planItems: DropdownItem[] = [
    { label: 'Sin vinculación a plan', value: '' },
    ...activePlans.map((p: TreatmentPlan) => {
      const obj = p.objetivoTerapeutico ?? '';
      return {
        label: `Plan ${p.numeroPlan} — ${obj.slice(0, 40)}${obj.length > 40 ? '…' : ''} (${p.progresoPorcentaje}%)`,
        value: p.id,
      };
    }),
  ];

  const selectedPlan = activePlans.find((p: TreatmentPlan) => p.id === planId);

  return (
    <>
      {/* ── Servicio ── */}
      {serviceItems.length > 0 && (
        <Box border="1px solid" borderColor={borderColor} borderRadius="14px" p="16px" bg={inputBg}>
          <Flex align="center" gap="6px" mb="10px">
            <Box color={sectionColor} w="13px" h="13px" flexShrink={0}>
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 12v4"/><path d="M16 6a2 2 0 0 1 1.414.586l4 4A2 2 0 0 1 22 12v7a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 .586-1.414l4-4A2 2 0 0 1 8 6z"/><path d="M16 6V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><path d="M2 14h20"/><path d="M8 12v4"/>
              </svg>
            </Box>
            <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="wider" color={sectionColor}>
              Servicio
            </Text>
            <Badge colorScheme="gray" fontSize="9px">opcional</Badge>
          </Flex>
          <Dropdown
            label={serviceId
              ? (() => { const s = serviceItems.find((s: ServiceItem) => s.id === serviceId); return s ? `${s.name} — $${Number(s.basePrice).toFixed(2)}` : 'Seleccionar del catálogo de servicios...'; })()
              : 'Seleccionar del catálogo de servicios...'}
            items={serviceItems.map((s: ServiceItem) => ({
              label: `${s.name} — $${Number(s.basePrice).toFixed(2)}`,
              value: s.id,
            }))}
            onSelect={val => handleServiceSelect(String(val))}
            w="100%"
            variant="outline"
            borderRadius="12px"
            menuWidth="100%"
            listMaxH="220px"
            itemPx="14px"
            itemPy="8px"
          />
        </Box>
      )}

      {/* ── Tipo de cita ── */}
      <Box border="1px solid" borderColor={borderColor} borderRadius="14px" p="16px" bg={inputBg}>
        <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="wider" color={sectionColor} mb="10px">
          Tipo de cita
        </Text>
        <Flex gap="8px" flexWrap="wrap">
          {([TipoCita.PRIMERA_VEZ, TipoCita.SEGUIMIENTO, TipoCita.INTERCONSULTA] as TipoCita[]).map(t => (
            <Box key={t} {...chip(tipoCita === t)} onClick={() => setTipoCita(t)}>
              {{ [TipoCita.PRIMERA_VEZ]: 'Consulta normal', [TipoCita.SEGUIMIENTO]: 'Seguimiento', [TipoCita.INTERCONSULTA]: 'Interconsulta' }[t]}
            </Box>
          ))}
        </Flex>
      </Box>

      {/* ── Fecha y hora ── */}
      <FormControl isInvalid={!!errors.scheduledAt}>
        <Box
          border="1px solid"
          borderColor={errors.scheduledAt ? 'red.400' : borderColor}
          borderRadius="14px"
          p="16px"
          bg={inputBg}
        >
          <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="wider" color={sectionColor} mb="12px">
            Fecha y hora
          </Text>
          <MiniCalendar
            selectedDate={selectedDate}
            onSelect={handleDateSelect}
          />
          <Box mt="12px">
            <Text fontSize="xs" fontWeight="700" color={sectionColor} textTransform="uppercase" letterSpacing="wider" mb="8px">
              Hora
            </Text>
            <Flex flexWrap="wrap" gap="6px">
              {TIME_SLOTS.map(slot => {
                const active = selectedTime === slot;
                return (
                  <Box
                    key={slot}
                    as="button"
                    onClick={() => handleTimeSelect(slot)}
                    px="10px"
                    py="5px"
                    borderRadius="20px"
                    fontSize="xs"
                    fontWeight="600"
                    border="1.5px solid"
                    borderColor={active ? 'brand.500' : borderColor}
                    bg={active ? 'brand.500' : 'transparent'}
                    color={active ? 'white' : mutedColor}
                    cursor="pointer"
                    transition="all 0.12s"
                    _hover={{ borderColor: 'brand.400', color: active ? 'white' : 'brand.500' }}
                  >
                    {slot}
                  </Box>
                );
              })}
            </Flex>
          </Box>
        </Box>
        {errors.scheduledAt && <FormErrorMessage ms="4px">{errors.scheduledAt}</FormErrorMessage>}
      </FormControl>

      {/* ── Episodio clínico ── */}
      <Collapse in={requiresEpisode} animateOpacity>
        <Box border="1px solid" borderColor={borderColor} borderRadius="14px" p="16px" bg={inputBg}>
          <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="wider" color={sectionColor} mb="10px">
            Episodio clínico
          </Text>
          <FormControl isInvalid={!!errors.episodeId}>
            {activeEpisodes.length === 0 ? (
              <Text fontSize="xs" color="orange.400" ms="4px">
                Este paciente no tiene episodios activos
              </Text>
            ) : (
              <Dropdown
                label={selectedEpisode
                  ? selectedEpisode.motivoConsulta + (selectedEpisode.diagnosticoPrincipal ? ` — ${selectedEpisode.diagnosticoPrincipal}` : '')
                  : 'Seleccionar episodio...'}
                items={episodeItems}
                onSelect={val => { setEpisodeId(String(val)); setErrors(p => ({ ...p, episodeId: '' })); }}
                w="100%"
                variant="outline"
                borderRadius="12px"
                listMaxH="260px"
                menuWidth="100%"
                itemPx="14px"
                itemPy="8px"
              />
            )}
            {errors.episodeId && <FormErrorMessage ms="4px">{errors.episodeId}</FormErrorMessage>}
          </FormControl>

          <Collapse in={!!episodeId && activePlans.length > 0} animateOpacity>
            <Box mt="16px" pt="14px" borderTop="1px solid" borderColor={borderColor}>
              <Flex align="center" gap="8px" mb="10px">
                <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="wider" color={sectionColor}>
                  Vincular a plan
                </Text>
                <Badge colorScheme="gray" fontSize="9px">opcional</Badge>
              </Flex>
              <Dropdown
                label={selectedPlan
                  ? `Plan ${selectedPlan.numeroPlan} — ${(selectedPlan.objetivoTerapeutico ?? '').slice(0, 40)}${(selectedPlan.objetivoTerapeutico ?? '').length > 40 ? '…' : ''}`
                  : 'Sin vinculación a plan'}
                items={planItems}
                onSelect={val => setPlanId(String(val))}
                w="100%"
                variant="outline"
                borderRadius="12px"
                menuWidth="100%"
                listMaxH="200px"
              />
            </Box>
          </Collapse>
        </Box>
      </Collapse>

      {/* ── Motivo ── */}
      <Box border="1px solid" borderColor={borderColor} borderRadius="14px" p="16px" bg={inputBg}>
        <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="wider" color={sectionColor} mb="10px">
          Motivo <Text as="span" fontSize="9px" fontWeight="400" color={mutedColor} textTransform="none" letterSpacing="normal">(opcional)</Text>
        </Text>
        <Textarea
          value={motivo}
          onChange={e => setMotivo(e.target.value)}
          placeholder="Ej: Control post-operatorio, dolor lumbar..."
          rows={2}
          maxLength={500}
          bg="transparent"
          border="none"
          borderRadius="0"
          fontSize="sm"
          resize="none"
          p="0"
          _focus={{ boxShadow: 'none' }}
        />
      </Box>

      {slotError && (
        <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="10px" px="14px" py="10px">
          <Text fontSize="sm" color="red.600" fontWeight="500">{slotError}</Text>
        </Box>
      )}

      {missingAvail && (
        <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="10px" px="14px" py="12px">
          <Text fontSize="sm" color="orange.700" fontWeight="600" mb="10px">
            ¿Configurar disponibilidad para este horario y agendar?
          </Text>
          <Flex gap="10px" mb="10px">
            <Box flex={1}>
              <Text fontSize="xs" color={mutedColor} mb="4px">Duración de sesión</Text>
              <Dropdown
                label={availDuration === 60 ? '1 h' : `${availDuration} min`}
                items={[
                  { label: '15 min', value: 15 },
                  { label: '30 min', value: 30 },
                  { label: '45 min', value: 45 },
                  { label: '1 h',    value: 60 },
                ]}
                onSelect={v => setAvailDuration(Number(v))}
                w="100%"
                variant="outline"
                size="sm"
              />
            </Box>
            <Box flex={1}>
              <Text fontSize="xs" color={mutedColor} mb="4px">Capacidad en paralelo</Text>
              <Dropdown
                label={availCapacity === 1 ? '1 paciente' : `${availCapacity} pacientes`}
                items={[
                  { label: '1 paciente',  value: 1 },
                  { label: '2 pacientes', value: 2 },
                  { label: '3 pacientes', value: 3 },
                ]}
                onSelect={v => setAvailCapacity(Number(v))}
                w="100%"
                variant="outline"
                size="sm"
              />
            </Box>
          </Flex>
          <Button
            w="full" size="sm" colorScheme="orange"
            leftIcon={<Icon as={MdEventAvailable} />}
            isLoading={createAvail.isPending}
            onClick={handleConfigureAvailability}
          >
            Configurar y agendar
          </Button>
        </Box>
      )}

      <Flex gap="10px" pt="4px">
        <Button flex="1" variant="outline" onClick={onBack}>
          ← Atrás
        </Button>
        <Button
          flex="2"
          colorScheme="brand"
          leftIcon={<Icon as={MdCalendarToday} />}
          isLoading={createAppt.isPending}
          onClick={handleSubmit}
        >
          Confirmar cita
        </Button>
      </Flex>
    </>
  );
}

// ─── Main Drawer ──────────────────────────────────────────────────────────────

export default function AppointmentDrawer({
  isOpen, onClose, initialScheduledAt, professionalId, onSuccess,
}: Props) {
  const [step,    setStep]    = useState<1 | 2>(1);
  const [patient, setPatient] = useState<Patient | null>(null);

  const headerColor = useColorModeValue('gray.800', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => { setStep(1); setPatient(null); }, 300);
    }
  }, [isOpen]);

  const slotLabel = React.useMemo(() => {
    if (!initialScheduledAt) return null;
    const d      = new Date(initialScheduledAt);
    const days   = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    const pad    = (n: number) => String(n).padStart(2, '0');
    return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, [initialScheduledAt]);

  const drawerTitle = (
    <Flex align="center" gap="12px" w="100%">
      <Box flexShrink={0} color={headerColor} w="28px" h="28px">
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12.127 22H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5.125"/>
          <path d="M14.62 18.8A2.25 2.25 0 1 1 18 15.836a2.25 2.25 0 1 1 3.38 2.966l-2.626 2.856a.998.998 0 0 1-1.507 0z"/>
          <path d="M16 2v4"/>
          <path d="M3 10h18"/>
          <path d="M8 2v4"/>
        </svg>
      </Box>
      <Box>
        <Text fontSize="md" fontWeight="800" color={headerColor}>Nueva Cita</Text>
        {slotLabel && <Text fontSize="xs" color={mutedColor}>{slotLabel}</Text>}
      </Box>
      <Box ms="auto" pr="40px" minW="220px">
        <Steps steps={DRAWER_STEPS} activeStep={step - 1} size="xs" />
      </Box>
    </Flex>
  );

  const drawerFooter = step === 1 && !patient ? (
    <Box borderTopWidth="1px" borderTopColor="gray.200" px="28px" py="16px">
      <Button variant="ghost" w="full" onClick={onClose}>Cancelar</Button>
    </Box>
  ) : undefined;

  return (
    <CustomDrawer
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={drawerTitle}
      footer={drawerFooter}
      contentPaddingX="38px"
      contentPaddingY="38px"
      layout="form"
      borderRadius="10px"
      borderRadiusSides="left"
    >
      <Text fontSize="lg" fontWeight="800" color={headerColor}>
        {DRAWER_STEPS[step - 1].title}
      </Text>

      {step === 1 && !patient && (
        <PatientSearch onSelect={p => { setPatient(p); setStep(2); }} />
      )}

      {step === 1 && patient && (
        <Box w="100%">
          <PatientCard patient={patient} onClear={() => setPatient(null)} />
          <Button mt="16px" w="full" colorScheme="brand" onClick={() => setStep(2)}>
            Continuar →
          </Button>
        </Box>
      )}

      {step === 2 && patient && (
        <Box w="100%" display="flex" flexDirection="column" gap="20px">
          <PatientCard patient={patient} onClear={() => { setStep(1); setPatient(null); }} />
          <ScheduleStep
            patient={patient}
            professionalId={professionalId}
            initialScheduledAt={initialScheduledAt}
            onSuccess={onSuccess}
            onBack={() => setStep(1)}
            onClose={onClose}
          />
        </Box>
      )}
    </CustomDrawer>
  );
}
